// One completed job sheet, read-only, on the engineer PWA (Session AEM Stream B2, extending the
// Session AEK history screens).
//
// Why this exists: the history feed's one-line summary is a taster. An engineer picking up a lift
// a colleague attended needs the record itself, and for a CALL-OUT most of all: what was
// reported, what was actually found, what was done, and what state the lift was left in. That is
// why the call-out block sits at the top of this screen, above the checklist.
//
// The checklist is grouped by the SAME fourteen customer-facing areas the engineer confirms on
// site and the certificate prints, through the shared pure grouper, with the same flat-list
// fallback for a goods / platform / equipment / legacy checklist that does not use them. No new
// grouping is invented here.
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { loadJobSheet } from "../../lib/offline/historyStore";
import { groupStoredChecklistByArea } from "../../lib/serviceAreas";
import { useOnline } from "../../lib/offline/useOnline";
import { jobTypeStyle } from "../../lib/jobType";
import JobTypeBadge from "../../components/JobTypeBadge";
import { api } from "../../lib/api";
import type {
  EngineerJobSheetDetail,
  EngineerJobSheetChecklistEntry,
  EngineerHistoryDefect,
} from "../../lib/api";

function dateLabel(iso: string | null): string {
  if (!iso) return "Date not recorded";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "Date not recorded"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

// LONDON wall clock, deliberately, and NOT the UTC the date above uses.
//
// The stored arrival and departure are real instants (the office and PWA capture paths build them
// from a typed local time), and every other surface that shows these two columns renders them in
// London: the office panel formats with no timeZone override on a UK device, and the customer
// call-out report PDF uses the explicitly Europe/London ukTimeHHMM. Rendering them in UTC here
// would put this screen an hour behind the certificate the customer holds for the seven months of
// British Summer Time, on exactly the attendance data the screen exists to hand over.
//
// dateLabel above stays UTC on purpose: it matches the AEK history rows this screen is opened
// from, so a visit cannot appear to change date between the list and the record.
function timeLabel(iso: string | null): string {
  if (!iso) return "Not recorded";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "Not recorded"
    : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
}

// The engineer's own recording language, and the same tones the office read view uses, so one
// record never reads two ways. The raw stored key is never rendered.
const CHECK_LABEL: Record<string, string> = { pass: "Pass", fail: "Fail", advisory: "Advisory", na: "N/A" };
const CHECK_TONE: Record<string, string> = {
  pass: "text-ele-green",
  fail: "text-ele-danger",
  advisory: "text-amber-400",
  na: "text-ele-body opacity-70",
};
const CHECK_MARK: Record<string, string> = { pass: "✓", fail: "✗", advisory: "!", na: "-" };

function severityLabel(severity: string): string {
  if (severity === "immediate") return "Immediate action";
  if (severity === "advisory") return "Advisory";
  return severity.replace(/_/g, " ");
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-6 font-menu text-xs uppercase tracking-wider text-ele-body">{children}</h2>;
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="mt-2">
      <p className="font-menu text-xs uppercase tracking-wider text-ele-body">{label}</p>
      <p className="mt-0.5 text-sm text-ele-strong">{value?.trim() || <span className="text-ele-body opacity-70">Not recorded</span>}</p>
    </div>
  );
}

// One checklist row: what was checked, how it came out, and any note the engineer left on it.
function CheckRow({ entry }: { entry: EngineerJobSheetChecklistEntry }) {
  const tone = CHECK_TONE[entry.status] ?? "text-ele-body";
  return (
    <li className="border-t border-ele-purple-accent/40 py-2 first:border-t-0">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-sm text-ele-strong">{entry.item}</p>
        <span className={`flex-none font-menu text-xs uppercase tracking-wider ${tone}`}>
          <span aria-hidden="true">{CHECK_MARK[entry.status] ?? ""} </span>
          {CHECK_LABEL[entry.status] ?? entry.status}
        </span>
      </div>
      {entry.note && <p className="mt-1 text-xs text-ele-body">{entry.note}</p>}
    </li>
  );
}

function FlatChecks({ entries }: { entries: EngineerJobSheetChecklistEntry[] }) {
  return (
    <ul className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3">
      {entries.map((entry, i) => (
        <CheckRow key={entry.id ?? `${entry.item}-${i}`} entry={entry} />
      ))}
    </ul>
  );
}

// Grouped by area when the stored checklist is the ELE-MS-001 passenger one, flat otherwise. Each
// area is collapsed behind its own summary so a 96-point checklist is scannable on a phone, and
// an area with anything flagged says so on the closed row: an engineer must not have to open
// fourteen sections to find the one that failed.
function ChecklistView({ entries }: { entries: EngineerJobSheetChecklistEntry[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  // The shared grouper is generic over an OPTIONAL id (it was written against the office DTO,
  // which omits the key rather than sending null). The wire DTO here is explicit about a missing
  // id, so the two are reconciled at the call site rather than by loosening either shape.
  const groups = groupStoredChecklistByArea(entries.map((e) => ({ ...e, id: e.id ?? undefined })));
  if (!groups) return <FlatChecks entries={entries} />;

  return (
    <ul className="mt-2 space-y-2">
      {groups.map((g) => {
        const flagged = g.fails + g.advisories;
        const summary =
          g.entries.length === 0
            ? "Not recorded"
            : flagged > 0
              ? `${g.fails > 0 ? `${g.fails} failed` : ""}${g.fails > 0 && g.advisories > 0 ? ", " : ""}${g.advisories > 0 ? `${g.advisories} advisory` : ""}`
              : "Pass";
        const tone = g.entries.length === 0 ? "text-ele-body opacity-70" : flagged > 0 ? "text-amber-400" : "text-ele-green";
        const isOpen = open[g.key] ?? false;
        return (
          <li key={g.key} className="rounded-ele border border-ele-purple-accent bg-ele-surface">
            <button
              type="button"
              onClick={() => setOpen((prev) => ({ ...prev, [g.key]: !isOpen }))}
              aria-expanded={isOpen}
              className="flex min-h-11 w-full items-center justify-between gap-3 px-3 text-left"
            >
              <span className="min-w-0 text-sm text-ele-strong">{g.label}</span>
              <span className={`flex-none font-menu text-xs uppercase tracking-wider ${tone}`}>{summary}</span>
            </button>
            {isOpen && (
              <div className="px-3 pb-2">
                {g.entries.length === 0 ? (
                  <p className="py-2 text-xs text-ele-body">Nothing recorded for this area on this visit.</p>
                ) : (
                  <ul>
                    {g.entries.map((entry, i) => (
                      <CheckRow key={entry.id ?? `${entry.item}-${i}`} entry={{ ...entry, id: entry.id ?? null }} />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// Defects raised from this visit. Same convention as the AEK lift-history screen: an immediate
// (Category A) defect takes the danger token and reads "Immediate action", never the same amber
// as a cosmetic advisory and never the raw enum key.
function DefectList({ defects }: { defects: EngineerHistoryDefect[] }) {
  const anyImmediate = defects.some((d) => d.severity === "immediate");
  const box = anyImmediate ? "border-ele-danger bg-[rgba(255,90,90,0.1)]" : "border-amber-500 bg-amber-500/10";
  return (
    <ul className={`mt-2 space-y-2 rounded-ele border p-3 ${box}`}>
      {defects.map((defect) => (
        <li key={defect.id}>
          <p
            className={`font-menu text-xs uppercase tracking-wider ${
              defect.severity === "immediate" ? "text-ele-danger" : "text-[var(--ele-amber-text)]"
            }`}
          >
            {severityLabel(defect.severity)}
            {defect.status === "resolved" ? " | Resolved" : " | Still open"}
          </p>
          <p className="text-sm text-ele-strong">{defect.description}</p>
        </li>
      ))}
    </ul>
  );
}

// A photo that will not load is reported as such and STOPS BEING A LINK, mirroring the office
// gallery's own failedIndexes handling. Without that the engineer gets a broken-image glyph whose
// natural retry (tapping it) navigates straight at the API and paints the raw error body, since
// the proxy answers a JSON error and nothing shapes it on the way to an anchor. Drive being
// unreachable is a live state on this platform, not a hypothetical.
function Photos({ jobId, count, online }: { jobId: string; count: number; online: boolean }) {
  const [failed, setFailed] = useState<Set<number>>(() => new Set());

  if (count === 0) return <p className="mt-2 text-sm text-ele-body">No photos taken on this visit.</p>;
  if (!online) {
    return (
      <p className="mt-2 text-sm text-ele-body">
        {count === 1 ? "1 photo was taken" : `${count} photos were taken`} on this visit. Photos need a connection to
        view.
      </p>
    );
  }

  const allFailed = failed.size >= count;
  return (
    <>
      <ul className="mt-2 grid grid-cols-3 gap-2">
        {Array.from({ length: count }, (_, i) => (
          <li key={i}>
            {failed.has(i) ? (
              <div className="flex aspect-square w-full items-center justify-center rounded-ele border border-ele-purple-accent bg-ele-inset-strong p-2 text-center">
                <span className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">
                  Photo unavailable
                </span>
              </div>
            ) : (
              <a href={api.engineer.jobSheetPhotoUrl(jobId, i)} target="_blank" rel="noopener noreferrer">
                <img
                  src={api.engineer.jobSheetPhotoUrl(jobId, i)}
                  alt={`Visit photo ${i + 1} (opens full size)`}
                  loading="lazy"
                  onError={() => setFailed((s) => (s.has(i) ? s : new Set(s).add(i)))}
                  className="aspect-square w-full rounded-ele border border-ele-purple-accent object-cover"
                />
              </a>
            )}
          </li>
        ))}
      </ul>
      {allFailed && (
        <p className="mt-2 text-xs text-ele-body">
          The photos for this visit cannot be loaded just now. Try again when you have a better connection.
        </p>
      )}
    </>
  );
}

export function EngineerJobSheetPage() {
  const { jobId = "" } = useParams();
  const location = useLocation();
  const online = useOnline();
  // Return to wherever this was opened from (a site's history or a lift's), falling back to the
  // browse list when the screen is reached directly, e.g. from a reloaded tab.
  const from = (location.state as { from?: string } | null)?.from ?? "/engineer/history";

  const [sheet, setSheet] = useState<EngineerJobSheetDetail | null>(null);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError(false);
    try {
      const res = await loadJobSheet(jobId);
      setSheet(res.data);
      setStale(res.stale);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }, [jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  const back = (
    <Link to={from} className="mb-3 inline-flex min-h-11 items-center font-menu text-xs tracking-wider text-ele-body">
      Back to history
    </Link>
  );

  if (busy && !sheet) {
    return (
      <div>
        {back}
        <p className="text-sm text-ele-body">Loading...</p>
      </div>
    );
  }

  if (error && !sheet) {
    return (
      <div>
        {back}
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-sm text-ele-body">
            {online
              ? "Could not load this record just now."
              : "This record has not been saved for offline viewing. Connect to the internet to view it."}
          </p>
          {online && (
            <button
              type="button"
              onClick={() => void load()}
              className="mt-3 inline-flex min-h-11 items-center rounded-ele border border-ele-field-border px-4 font-menu text-xs tracking-wider text-ele-body"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!sheet) return <div>{back}</div>;

  const style = jobTypeStyle(sheet.type);

  return (
    <div>
      {back}
      {stale && (
        <p className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-xs text-ele-body">
          Showing saved information. It will update when you are back online.
        </p>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Session AGZ: text-ele-strong to match every other engineer-screen h1 (this one
              inherited the dimmer bare-h1 body ink). */}
          <h1 className="font-heading text-2xl tracking-wide text-ele-strong">{dateLabel(sheet.completedAt)}</h1>
          <p className="mt-1 text-sm text-ele-body">
            {[sheet.eleNumber, sheet.siteName].filter(Boolean).join(" | ") || "Visit record"}
          </p>
          {sheet.siteAddress && <p className="text-xs text-ele-body opacity-70">{sheet.siteAddress}</p>}
        </div>
        <JobTypeBadge type={sheet.type} size="sm" label={style.label} />
      </div>

      <p className={`mt-3 text-sm ${sheet.outcome.attention ? "text-amber-400" : "text-ele-green"}`}>
        {sheet.outcome.label}
      </p>

      <p className="mt-2 text-xs text-ele-body">
        {sheet.engineerName ? `Completed by ${sheet.engineerName}` : "Engineer not recorded"}
        {sheet.jobNumber ? ` | Job ${sheet.jobNumber}` : ""}
      </p>
      <p className="text-xs text-ele-body opacity-70">
        {sheet.signature.signed
          ? `Signed by ${sheet.signature.signerName ?? "name not given"} on ${dateLabel(sheet.completedAt)}`
          : (sheet.signature.note ?? "No signature on file.")}
      </p>

      {/* A call-out's reported fault and its resolution are the most useful thing on this screen
          to a second engineer picking up a related problem, so they sit above everything else. */}
      {sheet.callout && (
        <section className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
          <h2 className="font-menu text-xs uppercase tracking-wider text-ele-blue">Call-out</h2>
          <Detail label="Fault reported" value={sheet.callout.faultReported} />
          <Detail label="Fault found" value={sheet.callout.faultFound} />
          <Detail label="Work carried out" value={sheet.callout.workCarriedOut} />
          <Detail label="Lift status on leaving" value={sheet.callout.liftStatusOnLeaving} />
          {(sheet.callout.arrivedAt || sheet.callout.leftAt) && (
            <p className="mt-2 text-xs text-ele-body opacity-70">
              Arrived {timeLabel(sheet.callout.arrivedAt)} | Left {timeLabel(sheet.callout.leftAt)}
            </p>
          )}
        </section>
      )}

      {sheet.defects.length > 0 && (
        <>
          <SectionHeading>
            {sheet.defects.length === 1 ? "Defect raised on this visit" : `${sheet.defects.length} defects raised on this visit`}
          </SectionHeading>
          <DefectList defects={sheet.defects} />
        </>
      )}

      {sheet.defectNotes.length > 0 && (
        <>
          <SectionHeading>Defect notes recorded on the visit</SectionHeading>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ele-strong">
            {sheet.defectNotes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </>
      )}

      <SectionHeading>Checklist</SectionHeading>
      {sheet.checklist.length === 0 ? (
        <p className="mt-2 text-sm text-ele-body">No checklist was recorded on this visit.</p>
      ) : (
        <ChecklistView entries={sheet.checklist} />
      )}
      {/* Said out loud rather than swallowed: a short list must never read as the whole visit. */}
      {sheet.checklistNotShown > 0 && (
        <p className="mt-2 text-xs text-amber-400">
          {sheet.checklistNotShown === 1
            ? "1 further check was recorded on this visit but cannot be listed here."
            : `${sheet.checklistNotShown} further checks were recorded on this visit but cannot be listed here.`}
        </p>
      )}

      {(sheet.engineerNotes || sheet.advisoryNotes) && (
        <>
          <SectionHeading>Notes</SectionHeading>
          {sheet.engineerNotes && <p className="mt-2 whitespace-pre-line text-sm text-ele-strong">{sheet.engineerNotes}</p>}
          {sheet.advisoryNotes && (
            <p className="mt-2 whitespace-pre-line text-sm text-amber-400">{sheet.advisoryNotes}</p>
          )}
        </>
      )}

      {sheet.parts.length > 0 && (
        <>
          <SectionHeading>Parts fitted</SectionHeading>
          <ul className="mt-2 space-y-1 text-sm text-ele-strong">
            {sheet.parts.map((part, i) => (
              <li key={i}>
                {part.quantity} x {part.description || "Part"}
                {part.partCode && <span className="text-ele-body"> ({part.partCode})</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      <SectionHeading>Photos</SectionHeading>
      <Photos jobId={sheet.jobId} count={sheet.photoCount} online={online} />
    </div>
  );
}
